/**
 * Fein-ACL-Editor pro (Modul, Subject) — Joerg 2026-08-20.
 *
 * Zeigt die Fein-Rechte-Matrix eines Moduls fuer eine Person, Gruppe oder
 * default-Zuweisung. Jede Zeile ist ein Permission-Slug aus dem Katalog
 * (portal_module_registry.permissions_catalog) — Haekchen setzbar auf:
 *   ✓ (allowed=true)   — explizit erlaubt
 *   ✗ (allowed=false)  — explizit verboten
 *   ─ (allowed=null)   — kein Eintrag, Level-Preset gilt (Fallback)
 *
 * „Preset anwenden": expandiert das gewaehlte Level (reader/tester/author/
 * editor/admin) in Haekchen — danach kann jedes Haekchen einzeln geaendert
 * werden. Ohne Preset-Klick bleibt der Fallback aktiv (─).
 *
 * REST-Endpoints (Portal-Backend admin-acl-permissions.ts):
 *   GET  /api/admin/acl/module-permissions?module=&subject_type=&subject_id=
 *   PUT  /api/admin/acl/module-permissions
 *   POST /api/admin/acl/module-permissions/apply-preset
 *
 * Wird eingebunden von:
 *   - AdressbuchPersonAkte.tsx (Zugriffe-Tab, subject_type='person')
 *   - AdminAdressbuchModuleDetailPage.tsx (Modul-Sicht, subject_type='person'|'group'|'default')
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@onvice/portal-shell-api';

type SubjectType = 'person' | 'group' | 'default';
type PresetLevel = 'reader' | 'tester' | 'author' | 'editor' | 'admin';
type Allowed = boolean | null;

interface CatalogEntry {
  slug: string;
  label: string;
  category?: string;
  presetLevel?: PresetLevel;
}
interface ValueEntry {
  permission_slug: string;
  allowed: boolean;
}
interface GetResponse {
  module: string;
  subject_type: SubjectType;
  subject_id: string | null;
  catalog: CatalogEntry[];
  values: ValueEntry[];
}

interface Props {
  moduleSlug: string;
  subjectType: SubjectType;
  subjectId: string | null;
  /** Aktueller ACL-Level des Subjects auf dem Modul — fuer den Preset-Button.
   *  Wenn undefined, wird kein Preset-Button angezeigt (Sub-Sicht ohne Level-Info). */
  currentLevel?: PresetLevel;
  /** Callback nach erfolgreichem Save/Preset — Parent kann Bestand neu laden. */
  onChanged?: () => void;
}

const PRESET_LEVELS: PresetLevel[] = ['reader', 'tester', 'author', 'editor', 'admin'];
const LEVEL_LABEL: Record<PresetLevel, string> = {
  reader: 'Leser',
  tester: 'Tester',
  author: 'Autor',
  editor: 'Editor',
  admin:  'Admin',
};

export default function ModulePermissionsMatrix({
  moduleSlug,
  subjectType,
  subjectId,
  currentLevel,
  onChanged,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [values, setValues] = useState<Map<string, Allowed>>(new Map());
  const [dirty, setDirty] = useState<Map<string, Allowed>>(new Map());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        module: moduleSlug,
        subject_type: subjectType,
      });
      if (subjectId) params.set('subject_id', subjectId);
      const res = await api.get<{ success: boolean; data: GetResponse }>(
        `/admin/acl/module-permissions?${params.toString()}`,
      );
      setCatalog(res.data.catalog);
      const m = new Map<string, Allowed>();
      for (const v of res.data.values) m.set(v.permission_slug, v.allowed);
      setValues(m);
      setDirty(new Map());
    } catch (e) {
      setError((e as Error).message || 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [moduleSlug, subjectType, subjectId]);

  useEffect(() => { void load(); }, [load]);

  const grouped = useMemo(() => {
    const g = new Map<string, CatalogEntry[]>();
    for (const c of catalog) {
      const key = c.category ?? 'Allgemein';
      const arr = g.get(key) ?? [];
      arr.push(c);
      g.set(key, arr);
    }
    return Array.from(g.entries()).map(([category, entries]) => ({ category, entries }));
  }, [catalog]);

  const effective = useCallback((slug: string): Allowed => {
    if (dirty.has(slug)) return dirty.get(slug) ?? null;
    return values.has(slug) ? (values.get(slug) ?? null) : null;
  }, [values, dirty]);

  const cycle = useCallback((slug: string) => {
    // ─ → ✓ → ✗ → ─
    const cur = effective(slug);
    const next: Allowed = cur === null ? true : cur === true ? false : null;
    setDirty((prev) => {
      const m = new Map(prev);
      m.set(slug, next);
      return m;
    });
  }, [effective]);

  const save = useCallback(async () => {
    if (dirty.size === 0) return;
    setSaving(true);
    setError(null);
    try {
      const changes = Array.from(dirty.entries()).map(([permission_slug, allowed]) => ({
        permission_slug,
        allowed,
      }));
      await api.put(`/admin/acl/module-permissions`, {
        module: moduleSlug,
        subject_type: subjectType,
        subject_id: subjectId,
        changes,
      });
      await load();
      onChanged?.();
    } catch (e) {
      setError((e as Error).message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }, [dirty, moduleSlug, subjectType, subjectId, load, onChanged]);

  const applyPreset = useCallback(async (preset: PresetLevel) => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/admin/acl/module-permissions/apply-preset`, {
        module: moduleSlug,
        subject_type: subjectType,
        subject_id: subjectId,
        preset,
      });
      await load();
      onChanged?.();
    } catch (e) {
      setError((e as Error).message || 'Preset fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }, [moduleSlug, subjectType, subjectId, load, onChanged]);

  if (loading) {
    return <div className="p-3 text-sm text-slate-500">Lade Fein-Rechte …</div>;
  }
  if (catalog.length === 0) {
    return <div className="p-3 text-sm text-slate-500">Dieses Modul hat keinen Fein-Rechte-Katalog.</div>;
  }

  return (
    <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-700">Preset anwenden:</span>
        {PRESET_LEVELS.map((lvl) => (
          <button
            key={lvl}
            type="button"
            disabled={saving}
            onClick={() => { void applyPreset(lvl); }}
            className={`rounded border px-2 py-1 text-xs font-medium transition ${
              currentLevel === lvl
                ? 'border-brand-600 bg-brand-50 text-brand-700'
                : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
            } disabled:opacity-50`}
            title={`Alle Haekchen fuer Level '${LEVEL_LABEL[lvl]}' setzen`}
          >
            {LEVEL_LABEL[lvl]}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-500">
          ─ = Fallback (Level-Preset gilt) · ✓ = erlaubt · ✗ = verboten
        </span>
      </div>

      {grouped.map(({ category, entries }) => (
        <div key={category} className="rounded border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
            {category}
          </div>
          <ul className="divide-y divide-slate-100">
            {entries.map((e) => {
              const val = effective(e.slug);
              const isDirty = dirty.has(e.slug);
              return (
                <li key={e.slug} className="flex items-center gap-3 px-3 py-1.5">
                  <button
                    type="button"
                    onClick={() => cycle(e.slug)}
                    className={`inline-flex h-7 w-7 items-center justify-center rounded border text-sm font-bold transition ${
                      val === true
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : val === false
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-slate-300 bg-white text-slate-400'
                    } ${isDirty ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                    title={val === true ? 'erlaubt (klick: verboten)' : val === false ? 'verboten (klick: Fallback)' : 'Fallback (klick: erlaubt)'}
                  >
                    {val === true ? '✓' : val === false ? '✗' : '─'}
                  </button>
                  <div className="flex flex-1 flex-col">
                    <span className="text-slate-800">{e.label}</span>
                    <span className="font-mono text-[11px] text-slate-500">
                      {e.slug}
                      {e.presetLevel && (
                        <span className="ml-2 text-slate-400">Preset ab: {LEVEL_LABEL[e.presetLevel]}</span>
                      )}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {error && <div className="rounded bg-red-100 px-3 py-2 text-xs text-red-700">{error}</div>}

      <div className="flex items-center justify-end gap-2">
        {dirty.size > 0 && (
          <button
            type="button"
            disabled={saving}
            onClick={() => setDirty(new Map())}
            className="rounded border border-slate-300 bg-white px-3 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Verwerfen ({dirty.size})
          </button>
        )}
        <button
          type="button"
          disabled={saving || dirty.size === 0}
          onClick={() => { void save(); }}
          className="rounded bg-brand-600 px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-brand-500 disabled:opacity-50"
        >
          {saving ? 'Speichere …' : dirty.size === 0 ? 'Keine Aenderungen' : `Speichern (${dirty.size})`}
        </button>
      </div>
    </div>
  );
}
